// OnboardingReturnCapture — non-visual landing effect for signed onboarding return links (49-16).
//
// Email and WhatsApp links carry `ort=<signed token>`. Landing on any page with it POSTs the token
// once from page JavaScript, then strips `ort` from the URL. Nothing stamps on a plain GET, so link
// scanners and previewers never count as a return (D-01, D-26, T-49-03); the server verifies the
// token and enforces the 24h floor (plan 49-20). Runs signed in or signed out (the POST carries no
// auth header). Activation is best effort: errors (including a 404 before the route ships) are
// swallowed and never affect the page.
//
// Ownership rule that keeps this race-free with OnboardingReturnBanner whichever effect runs first:
// this component only ever reads and strips `ort`; the banner only ever reads and strips `ov`.
//
// Mounted inside BrowserWidgets (+Layout.tsx), so it never runs during SSR.

import { useEffect } from "react";
import { usePageContext } from "vike-react/usePageContext";
import { RETURN_TOKEN_PARAM } from "@shared/onboarding";
import { readReturnParams } from "@/lib/onboardingUi";
import { postReturnToken } from "@/api/onboardingService";

// Survives remounts (route changes re-render the layout), so one token is POSTed once per page load.
let capturedFor: string | null = null;

export default function OnboardingReturnCapture() {
  // Re-check on client-side navigation too (a link opened inside the app), not only the first load.
  const urlOriginal = usePageContext().urlOriginal;
  useEffect(() => {
    if (typeof window === "undefined") return;
    const { token } = readReturnParams(window.location.search);
    if (!token) return;
    if (capturedFor !== token) {
      capturedFor = token;
      void postReturnToken(token).catch(() => undefined);
    }
    const params = new URLSearchParams(window.location.search);
    params.delete(RETURN_TOKEN_PARAM);
    const qs = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`,
    );
  }, [urlOriginal]);

  return null;
}
